/*
 * @providesModule MatchSelectController
 *
 */

import AddFriendModal from 'AddFriendModal';
import AWSManager from 'AWSManager';
import Bracket from 'SKZBracket';
import BracketStore from 'BracketStore';
import ClientPropertyConstants from 'ClientPropertyConstants';
import FriendsTournamentController from 'FriendsTournamentController';
import FriendsTournamentStore from 'FriendsTournamentStore';
import FTUEController from 'FTUEController';
import GlobalEventEmitter from 'GlobalEventEmitter';
import I18n from 'INTL';
import LoadingIndicator from 'LoadingIndicator';
import MatchJoinController from 'MatchJoinController';
import {Navigator} from 'react-native-deprecated-custom-components';
import PlayableMatchesStore from 'PlayableMatchesStore';
import PLOWDisplayHandler from 'PLOWDisplayHandler';
import Preferences from 'Preferences';
import {PLAY_TAB_LAST_SELECTED_CATEGORY} from 'SKZConstants';
import Tournament from 'SKZTournament';
import SkillzRouter from 'SkillzRouter';
import SkillzModule from 'SkillzModule';

import {Alert, Linking} from 'react-native';

class MatchSelectController {
  // TOURNAMENT JOIN

  handleJoinTappedCell(
    tournament: Tournament,
    navigator: Navigator,
    matchCode?: string,
    setID: number,
    isBotMatch: boolean = false,
    goStraightIntoMatch: boolean = false
  ) {
    this.beginJoinFlowForTournament(
      tournament,
      navigator,
      null,
      setID,
      isBotMatch,
      goStraightIntoMatch
    );
  }

  beginJoinFlowForTournament(
    tournament: Tournament,
    navigator: Navigator,
    matchCode?: string,
    setID: number,
    isBotMatch: boolean = false,
    goStraightIntoMatch: boolean = false
  ) {
    if (!FTUEController.hasSeenMatchSelectTutorial() || tournament instanceof Array) {
      MatchJoinController.displayJoinFlowForTournament(
        tournament,
        navigator,
        null,
        false,
        setID,
        isBotMatch
      );
    } else if (tournament.live_event) {
      this._handleJoinLiveEvent(tournament, navigator, matchCode);
    } else {
      if (tournament.isFriendTournament() && !tournament.isIncomingInvite()) {
        MatchJoinController.validateTournamentJoin(
          tournament,
          true,
          navigator,
          (success: boolean) => {
            if (success) {
              FriendsTournamentStore.setSelectedTournament(tournament);
              FriendsTournamentController.goToInviteFriends(tournament);
            }
          }
        );
      } else {
        MatchJoinController.displayJoinFlowForTournament(
          tournament,
          navigator,
          null,
          false,
          null,
          false,
          goStraightIntoMatch
        );
      }
    }
  }

  // Joins given tournament that was fetched with given match Id. If targetTournamentId is provided, only joins given tournament if it matches (to be used in the case of entering a required event code).
  handleJoinFetchedTournament(tournament: Tournament, matchCode: string, navigator: Navigator) {
    tournament.insertTournamentIntoCoreData(() => {
      AddFriendModal.showIfNeeded(tournament, true, false, (goodToJoin: boolean) => {
        if (goodToJoin) {
          this._attemptFetchedTournamentJoin(tournament, matchCode, navigator);
        }
      });
    });
  }

  _attemptFetchedTournamentJoin(tournamentData: Object, matchCode: string, navigator: Navigator) {
    const tournament = new Tournament(tournamentData);
    if (tournament.bracket) {
      this._beginBracket(tournament, navigator);
    } else {
      MatchJoinController.displayJoinFlowForTournamentWithCode(tournament, navigator, matchCode);
    }
  }

  _handleJoinLiveEvent(tournament: Tournament, navigator: Navigator, matchCode?: string) {
    if (
      tournament.live_event.match_code_required &&
      !tournament.isPlayerRegistered() &&
      !matchCode
    ) {
      this._showMatchCodeEntry(tournament);
    } else if (tournament.bracket) {
      this._beginBracket(tournament, navigator);
    } else {
      if (matchCode) {
        MatchJoinController.displayJoinFlowForTournamentWithCode(tournament, navigator, matchCode);
      } else {
        MatchJoinController.displayJoinFlowForTournament(tournament, navigator);
      }
    }
  }

  _showMatchCodeEntry(tournament: Tournament) {
    GlobalEventEmitter.emit('EnterMatchCode', tournament);
  }

  _beginBracket(tournament: Tournament, navigator: Navigator) {
    if (!tournament.hasTournamentStarted()) {
      if (tournament.isBracketFull() && !tournament.isPlayerRegistered()) {
        this._alertFullBracket(tournament);
      } else {
        MatchJoinController.displayJoinFlowForTournament(tournament, navigator);
      }
    } else {
      this._beginBracketStarted(tournament, navigator);
    }
  }

  _alertFullBracket(tournament: Tournament) {
    const messageFullBracketWithoutWatchLine = I18n.message(
      'Sorry, the tournament "{0}" is all booked up. Join a different one for a chance to win amazing prizes!',
      tournament.name
    );
    const messageFullBracketWithWatchLine = I18n.message(
      'Sorry, {0} is full. Watch the tournament after it starts by clicking on the tournament, or join another tournament.',
      tournament.name
    );

    const messageFullBracket = ClientPropertyConstants.getLiveBracketsSkipStreamFlow()
      ? messageFullBracketWithoutWatchLine
      : messageFullBracketWithWatchLine;

    Alert.alert(messageFullBracket, null, [{text: I18n.message('OK')}]);
  }

  _beginBracketStarted(tournament: Tournament, navigator: Navigator) {
    if (tournament.isPlayerRegistered()) {
      this._fetchAndDisplayBracketPlayScreen(tournament, navigator);
    } else {
      // For live brackets, navigate to the brackets screen to allow users to view the tournament
      if (tournament.isLiveBracket()) {
        const GAME_ID = SkillzModule.App.GameId;
        console.log(
          'MatchSelectController: Navigating to live brackets with tournament:',
          tournament
        );
        console.log('MatchSelectController: Tournament bracket:', tournament.bracket);
        console.log('MatchSelectController: Tournament bracket ID:', tournament.bracket?.id);
        SkillzRouter.push(`/games/${GAME_ID}/brackets-v2`, {
          tournament,
          hideOrientationFlip: true,
          isFromPlayTab: true,
          isSkipRerender: true,
        });
      } else if (tournament.live_event.stream_url) {
        //Display stream url for non-live-bracket live events
        Linking.openURL(tournament.live_event.stream_url);
      } else {
        //Once the streaming page is available publically, re-enable this line and remove the alert.
        //Linking.openURL(Bracket.getBracketBaseURL(tournament.bracket.id));
        const messageFullBracketWithoutWatchLine = I18n.message(
          'Sorry, the tournament "{0}" is all booked up. Try a different match for a chance to win amazing prizes!',
          tournament?.name
        );

        Alert.alert(I18n.message('Bracket Full'), messageFullBracketWithoutWatchLine, [
          {text: I18n.message('OK')},
        ]);
      }
    }
  }

  _fetchAndDisplayBracketPlayScreen(tournament: Tournament, navigator: Navigator) {
    //Fetch match and user id
    //Decide whether to display PLOW or Versus
    LoadingIndicator.show();
    BracketStore.fetchBracket(tournament.bracket.id, (bracket: Bracket) => {
      LoadingIndicator.hide();
      if (!bracket) {
        return;
      }

      const userId = bracket.player.user_id;
      const match = bracket.getLastMatchForUser(userId);

      if (tournament.isPlayerMatchReadyToPlay()) {
        MatchJoinController.handleJoinForLiveBracket(
          bracket.player.latest_position,
          tournament,
          match,
          userId,
          navigator
        );
      } else if (tournament.isPlayerMatchFinished()) {
        PLOWDisplayHandler.displayMatchResults(
          match,
          false,
          false,
          `${userId}`,
          false,
          () => {},
          false
        );
      } else {
        AWSManager.trackEvent('VIEW_PLOW_MATCH_SELECT', {
          id: match.id,
          tournament_id: tournament.id,
        });
        PLOWDisplayHandler.displayResultsFromMatchStream(match, userId, true, false, false);
      }
    });
  }

  /**
   * Allow us to know if the category should be auto-expanded in the Match Select List
   * @param {string} categoryType the type of category
   * @param {number} userNumberGamePlayed the number of game played
   * @param {boolean} inFTUE if user is still concidered in ftue
   * @returns {boolean} If should force category to be expanded
   */
  shouldForceExpandCategory = (
    categoryType: string,
    userNumberGamePlayed: number,
    inFTUE?: boolean
  ): boolean => {
    const H2H = 'CASH'; //tech debt : those should be in a sharable enum
    const LIVE_EVENTS = 'LIVE';

    if (categoryType != H2H && categoryType != 'RECOMMENDED' && categoryType != LIVE_EVENTS) {
      return false;
    }

    if (inFTUE) {
      return false;
    }

    // Handle RECOMMENDED category
    if (
      categoryType == 'RECOMMENDED' &&
      !PlayableMatchesStore.hasCategoryBeenToggled(categoryType)
    ) {
      return true;
    }

    // Handle LIVE_EVENTS category (same logic as H2H)
    if (categoryType == LIVE_EVENTS) {
      if (ClientPropertyConstants.getAutoExpandLiveTournamentTemplatesAfterXMatches() < 0) {
        return false;
      }

      if (PlayableMatchesStore.hasCategoryBeenToggled(categoryType)) {
        return false;
      }

      return (
        ClientPropertyConstants.getAutoExpandLiveTournamentTemplatesAfterXMatches() <=
        userNumberGamePlayed
      );
    }

    // Handle H2H category (existing logic)
    if (ClientPropertyConstants.getAutoExpandProH2HAfterXMatches() < 0) {
      // property doesn't enable auto-expansion
      return false;
    }

    if (PlayableMatchesStore.hasCategoryBeenToggled(categoryType)) {
      // selected/toggled before by user
      return false;
    }

    return ClientPropertyConstants.getAutoExpandProH2HAfterXMatches() <= userNumberGamePlayed; //property threshold passed
  };
}

export default new MatchSelectController();
